import { Row, Col, Select, Button, Checkbox, Typography } from 'antd'
import { useEffect } from 'react'
import styled from 'styled-components'
import { StyledSubTable } from './styledComponents'
import { useTranslation } from 'react-i18next'
import { PERCENT, CENT, summarizeDeliveryTimes, centToEuro } from '../utils'
import Form from '$components/Form'
import FollowUpTreatmentsView from '$components/FollowUpTreatmentsView'
import MaterialChangeLogView from '$components/MaterialChangeLogView'

const { Text } = Typography

const StyledCol = styled(Col)`
  padding: 25px;
`

const StyledSubTableWithHighlight = styled(StyledSubTable)`
  .selected-row {
    background-color: #e6f7ff !important;
    border: 1px solid #91d5ff;
  }

  .selected-row:hover {
    background-color: #bae7ff !important;
  }
`

const OrderPartDetailView = ({
  orderPartListId,
  orderPart: _originalOrderPart,
  editable,
}) => {
  // transform
  const orderPart = {
    ..._originalOrderPart,
    provider_offers: _originalOrderPart.provider_offers
      ? Object.fromEntries(
          _originalOrderPart.provider_offers.map((offer) => [
            offer.offer_id,
            offer,
          ]),
        )
      : {},
  }

  const { t } = useTranslation()
  const [form] = Form.useForm()
  const providerOffersValues = Form.useWatch('provider_offers', form)

  const isOfferSelected = providerOffersValues
    ? Object.values(providerOffersValues).some(
        (offer) => offer && offer.selected,
      )
    : false

  useEffect(() => {
    if (editable) {
      const updatedOffers = {}
      Object.entries(orderPart.provider_offers).forEach(([offerId, offer]) => {
        if (!offer.delivery_days) {
          updatedOffers[offerId] = {
            delivery_days: offer.base_production_time,
          }
        }
      })
      if (Object.keys(updatedOffers).length > 0) {
        form.setFieldsValue({ provider_offers: updatedOffers })
      }
    }
  }, [editable, orderPart.provider_offers, form])

  const handleCheckboxChange = (offerId, checked) => {
    const currentOffers = form.getFieldValue('provider_offers') || {}
    const updatedOffers = {
      ...currentOffers,
      [offerId]: {
        ...currentOffers[offerId],
        selected: checked,
      },
    }
    form.setFieldsValue({ provider_offers: updatedOffers })
  }

  const renderBoolAsText = (val) => (val ? t('yes') : t('no'))
  const renderUnitAsText = (val) =>
    ({
      [PERCENT]: '%',
      [CENT]: '€',
    })[val]

  return (
    <div style={{ backgroundColor: '#fdfdfd' }}>
      <Form
        name={[orderPartListId, orderPart.id]}
        form={form}
        initialValues={orderPart}
      >
        <Row>
          <StyledCol span={24}>
            {orderPart.is_oam_changed && (
              <MaterialChangeLogView orderPart={orderPart} />
            )}
            <FollowUpTreatmentsView orderPart={orderPart} />
            <h3>{t('service.order_part.batch_offers')}</h3>
            <StyledSubTableWithHighlight
              dataSource={Object.values(orderPart.provider_offers)}
              columns={[
                {
                  title: t('Amount'),
                  dataIndex: 'amount',
                  key: 'amount',
                },
                {
                  title: t('Price per unit'),
                  key: 'base_price',
                  render: (record) =>
                    `${(record.base_price / 100).toFixed(2)} €`,
                },
                {
                  title: t('received_requests.total_batch_price'),
                  key: 'total',
                  render: (record) => {
                    const total = record.amount * record.base_price
                    return `${(total / 100).toFixed(2)} €`
                  },
                },
                {
                  title: t('received_requests.delivery_time'),
                  render: (_text, record) => {
                    if (editable) {
                      const isSelected = record.selected
                      return (
                        <Form.Item
                          name={[
                            'provider_offers',
                            record.offer_id,
                            'delivery_days',
                          ]}
                          style={{ marginBlock: '0px' }}
                          rules={
                            isSelected
                              ? [
                                  {
                                    required: true,
                                    message: t('my_orders.choose_option'),
                                  },
                                ]
                              : []
                          }
                        >
                          <Select
                            size="small"
                            popupMatchSelectWidth={false}
                            disabled={!isSelected}
                            options={[
                              {
                                value: record.base_production_time,
                                label: (
                                  <>
                                    {record.base_production_time} {t('days')}
                                  </>
                                ),
                              },
                              ...record.production_time_rules.map((rule) => ({
                                value: rule.max_days,
                                label: (
                                  <>
                                    {rule.max_days} {t('days')}{' '}
                                    <Text type="secondary">
                                      (+
                                      {rule.surcharge_unit === CENT
                                        ? centToEuro(rule.surcharge_amount)
                                        : rule.surcharge_amount}
                                      {renderUnitAsText(rule.surcharge_unit)}{' '}
                                      per part)
                                    </Text>
                                  </>
                                ),
                              })),
                            ]}
                          />
                        </Form.Item>
                      )
                    } else {
                      return t(
                        'received_requests.days',
                        summarizeDeliveryTimes([record.delivery_days]),
                      )
                    }
                  },
                },
                {
                  title: t('service.order_part.partial_short_text'),
                  dataIndex: 'allow_partial_delivery',
                  render: (value) => renderBoolAsText(value),
                },
                {
                  title: t('service.order_part.subcontractors_short_text'),
                  dataIndex: 'allow_subcontractors',
                  render: (value) => renderBoolAsText(value),
                },
                {
                  title: t('service.order_part.other_suppliers_short_text'),
                  dataIndex: 'allow_other_suppliers',
                  render: (value) => renderBoolAsText(value),
                },
                {
                  title: t('service.order_part.select_short_text'),
                  render: (record) => {
                    return editable ? (
                      <Form.Item
                        name={['provider_offers', record.offer_id, 'selected']}
                        valuePropName="checked"
                        style={{ marginBlock: '0px' }}
                      >
                        <Checkbox
                          onChange={(e) =>
                            handleCheckboxChange(
                              record.offer_id,
                              e.target.checked,
                            )
                          }
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name={['provider_offers', record.offer_id, 'selected']}
                        valuePropName="checked"
                        style={{ marginBlock: '0px' }}
                      >
                        <Checkbox disabled />
                      </Form.Item>
                    )
                  },
                },
              ]}
              rowKey={(record) => record.offer_id}
              rowClassName={(record) => {
                const isSelected = record.selected
                return isSelected ? 'selected-row' : ''
              }}
              size="small"
              style={{ width: '100%' }}
              pagination={false}
            />
          </StyledCol>
        </Row>
        {editable && (
          <Row justify={'end'} gutter={8} style={{ padding: 10 }}>
            <Col>
              <Button onClick={() => form.resetFields()}>
                {t('button.reset')}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                disabled={!isOfferSelected}
              >
                {t('button.save')}
              </Button>
            </Col>
          </Row>
        )}
      </Form>
    </div>
  )
}

export default OrderPartDetailView
